"use client";

import { clsx } from "clsx";
import { Icon, type IconProps } from "../Icon";
import { AnimatePresence, motion } from "framer-motion";
import { Loading } from "../Loading";

type Merge<T, U> = DistributiveOmit<T, keyof U> & U;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type ButtonElementType = React.ElementType<any, "button" | "a">;

type ButtonProps<T extends ButtonElementType = "button"> = Merge<
  React.ComponentPropsWithoutRef<T>,
  {
    loading?: boolean;
    size?: "sm" | "md" | "lg";
    variant?:
      | "primary"
      | "secondary"
      | "destructive"
      | "warning"
      | "secondaryWarning";
    block?: boolean;
    leadingIcon?: IconProps["icon"];
    trailingIcon?: IconProps["icon"];
    as?: T;
  } & (T extends "button"
    ? Pick<
        JSX.IntrinsicElements["button"],
        | "className"
        | "onClick"
        | "onMouseEnter"
        | "onMouseLeave"
        | "children"
        | "disabled"
        | "type"
        | "tabIndex"
      >
    : T extends "a"
      ? Pick<
          JSX.IntrinsicElements["a"],
          | "className"
          | "onClick"
          | "onMouseEnter"
          | "onMouseLeave"
          | "children"
          | "type"
          | "tabIndex"
          | "href"
        > & { disabled?: never }
      : undefined)
>;

// Exported for reuse by LinkButton to ensure visual consistency between
// Button and LinkButton without duplicating style definitions.
export const containerStyles = {
  base: "antialiased font-semibold cursor-pointer relative inline-flex items-center justify-center font-medium whitespace-nowrap outline-none focus-visible:ring-2 ring-offset-1 active:scale-97 ring-theme-accent transition duration-150 ease-in-out",
  size: {
    sm: "h-9 px-3 rounded-md",
    md: "h-10 px-4 rounded-md",
    lg: "h-12 px-4 rounded-md",
  },
  variant: {
    primary:
      "bg-theme-button-primary-background-default hover:bg-theme-button-primary-background-hover text-theme-button-primary-text-default shadow-button",
    secondary: "bg-white border border-default hover:bg-gray-300 text-black",
    destructive:
      "bg-white border border-default hover:bg-gray-300 text-red-600",
    warning: "bg-signal-warning hover:bg-signal-warning/80 text-black",
    secondaryWarning:
      "bg-white border border-default hover:bg-gray-300 text-black",
  },
  block: "flex w-full",
  disabled: "opacity-50 pointer-events-none",
};

export const innerStyles = {
  base: "inline-flex space-x-2 items-center",
};

export const loadingStyles = {
  variant: {
    primary: "text-theme-button-primary-text-default",
    secondary: "text-black",
    destructive: "text-red-600",
    warning: "text-black",
    secondaryWarning: "text-black",
  },
};

export const iconStyles = {
  variant: {
    primary: "text-theme-button-primary-text-default opacity-75",
    secondary: "text-black opacity-75",
    destructive: "text-red-600 opacity-75",
    warning: "text-black opacity-90",
    secondaryWarning: "text-signal-warning",
  },
  size: {
    sm: "text-base",
    md: "text-base",
    lg: "text-base",
  },
};

const MotionLoading = motion(Loading);

export const Button = <T extends ButtonElementType = "button">({
  as = "button" as T,
  size = "md",
  variant = "primary",
  leadingIcon,
  trailingIcon,
  block,
  children,
  loading,
  disabled,
  className,
  ...props
}: ButtonProps<T>) => {
  const Element: ButtonElementType = as;

  return (
    <Element
      className={clsx([
        containerStyles.base,
        containerStyles.size[size],
        containerStyles.variant[variant],
        disabled && containerStyles.disabled,
        block && containerStyles.block,
        className,
      ])}
      disabled={loading || disabled}
      {...props}
    >
      <AnimatePresence mode="wait">
        {loading && (
          <MotionLoading
            initial={{ opacity: 0, position: "absolute", scale: 0.75 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.75 }}
            className={clsx([loadingStyles.variant[variant]])}
            size="sm"
            variant={variant === "primary" ? "light" : "default"}
          />
        )}
      </AnimatePresence>
      <motion.div
        className={clsx([innerStyles.base])}
        animate={{ opacity: loading ? 0 : 1, scale: loading ? 0.95 : 1 }}
      >
        {leadingIcon && (
          <Icon
            className={clsx([
              iconStyles.variant[variant],
              iconStyles.size[size],
            ])}
            icon={leadingIcon}
          />
        )}
        <div>{children}</div>
        {trailingIcon && (
          <Icon
            className={clsx([
              iconStyles.variant[variant],
              iconStyles.size[size],
            ])}
            icon={trailingIcon}
          />
        )}
      </motion.div>
    </Element>
  );
};

Button.displayName = "Button";
